import { useLocation } from 'react-router-dom';

import { IconApi } from '@components/icons/api';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconCompany } from '@components/icons/company';
import { IconEmail } from '@components/icons/email';
import { IconPlan } from '@components/icons/icon-plan';
import { IconIntegration } from '@components/icons/integration';
import { IconLock } from '@components/icons/lock';
import { IconPayment } from '@components/icons/payment';
import { IconPerson } from '@components/icons/person';
import { IconPortal } from '@components/icons/portal';
import { IconSSO } from '@components/icons/sso';
import { IconStatistics } from '@components/icons/statistics';
import { IconTeam } from '@components/icons/team';
import { Nav } from '@components/nav';
import { Link, NavGroupCollapse } from '@components/nav-group-collapse/nav-group-collapse';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { createSelectionStore } from '@stores/create-selection-store';

import { usePlan } from 'api/authentication/hooks';
import { useFeature } from 'ui/hooks';

import { routePaths } from '../../navigation';

type LocationState = {
  from: string;
};

type Group = {
  id: string;
  label: string;
  links: Link[];
};

const expandedStore = createSelectionStore({ persistKey: 'company-nav-state' });

export const SettingsNavbar = () => {
  const { t } = useScopedTranslate(locale);
  const location = useLocation();
  const { from } = (location.state as LocationState) || { from: '/' };
  const plan = usePlan();
  const { isEnabled: hasMCPServers } = useFeature('FEATURE_MCP_SERVERS');

  const groups: Group[] = [
    {
      id: 'organization',
      label: t('organization'),
      links: [
        {
          id: 'organization-general',
          href: routePaths.settingsGeneral,
          icon: <IconCompany size={16} />,
          label: t('general'),
        },
        {
          id: 'organization-users',
          href: routePaths.settingsUsers,
          icon: <IconTeam size={16} />,
          label: t('users'),
        },
        {
          id: 'organization-email-domain',
          href: routePaths.settingsEmailDomain,
          icon: <IconEmail size={16} />,
          label: t('emailDomain'),
        },
        {
          id: 'organization-security',
          href: routePaths.settingsOrganizationSecurity,
          icon: <IconSSO size={16} />,
          label: t('security'),
        },
        {
          id: 'organization-integrations',
          href: routePaths.integrations,
          icon: <IconIntegration size={16} />,
          label: t('integrations'),
        },
        ...(hasMCPServers
          ? [
              {
                id: 'organization-mcp-servers',
                href: routePaths.mcpServers,
                icon: <IconIntegration size={16} />,
                label: t('mcpServers'),
              },
            ]
          : []),
      ],
    },
    ...(plan
      ? [
          {
            id: 'subscription',
            label: t('subscription'),
            links: [
              {
                id: 'subscription-billing',
                href: routePaths.subscriptionBilling,
                icon: <IconPayment size={16} />,
                label: t('billing'),
              },
              {
                id: 'subscription-plans',
                href: routePaths.subscriptionPlans,
                icon: <IconPlan size={16} />,
                label: t('plans'),
              },
            ],
          },
        ]
      : []),
    {
      id: 'portal',
      label: t('portal'),
      links: [
        {
          id: 'portal-portals',
          href: routePaths.portals,
          icon: <IconPortal size={16} />,
          label: t('portals'),
        },
        {
          id: 'portal-statistics',
          href: routePaths.portalStatistics,
          icon: <IconStatistics size={16} />,
          label: t('statistics'),
        },
      ],
    },
    {
      id: 'account',
      label: t('account'),
      links: [
        {
          id: 'account-settings',
          href: routePaths.settingsProfile,
          icon: <IconPerson size={16} />,
          label: t('profile'),
        },
        {
          id: 'account-security',
          href: routePaths.settingsAccountSecurity,
          icon: <IconLock size={16} />,
          label: t('security'),
        },
        {
          id: 'account-api',
          href: routePaths.settingsApi,
          icon: <IconApi size={16} />,
          label: t('api'),
        },
      ],
    },
  ];

  return (
    <Nav
      stateStorageKey="company-nav-state"
      variant="static"
      fixedHeader={
        <Nav.Header
          logo={
            <Nav.Link
              href={from}
              label={<Text font="bold14" caps text={t('back')} color="white100" />}
              iconLeft={<IconArrowLeft size={16} />}
            />
          }
        />
      }
    >
      {groups.map((group) => (
        <NavGroupCollapse
          key={group.id}
          id={group.id}
          label={group.label}
          links={group.links}
          toggleGroup={expandedStore.toggle}
          expandedSelectionStore={expandedStore}
        />
      ))}
      <Nav.Spacer />
    </Nav>
  );
};

const locale = {
  en: {
    organization: 'Organization',
    general: 'General',
    users: 'Users',
    emailDomain: 'Email domain',
    account: 'Account',
    profile: 'Profile',
    security: 'Security',
    adminSettings: 'Admin settings',
    back: 'Back',
    team: 'Team',
    templates: 'Templates',
    api: 'Api',
    integrations: 'Integrations',
    lists: 'Lists',
    documents: 'Documents',
    workflowHub: 'Workflow hub',
    journeyFieldTemplates: 'Journey fields',
    portal: 'Portal',
    subscription: 'Subscription',
    plans: 'Plans',
    billing: 'Billing',
    mcpServers: 'MCP Servers',
    portals: 'Portals',
    statistics: 'Statistics',
  },
};
